import React, { useState, useRef, useCallback } from 'react';
import { streamOpenAIResponse } from '../utils/openai';
import ReactMarkdown from 'react-markdown';

const SelectionChatWindow = ({ selectedText, onClose, position, currentPaper }) => {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [windowPosition, setWindowPosition] = useState(position);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const abortControllerRef = useRef(null);

    const getSystemPrompt = useCallback(() => {
        return `You are a Research Assistant Agent that clears doubts from research paper. \
        Your responses will be rendered in markdown format. \
        User will provide you a selected text from a research papers and you should give proper explanation to the user's question. \
        You will be provided with the paper title and abstract.


Selected Text:
${selectedText}

\n

Paper Title:
${currentPaper?.title || 'N/A'}

\n

Abstract:
${currentPaper?.summary || 'N/A'}`;
    }, [selectedText, currentPaper]);

    const handleSend = useCallback(async () => {
        if (!inputText.trim() || isLoading) return;

        const newMessage = {
            role: 'user',
            content: inputText
        };

        setMessages(prev => [...prev, newMessage]);
        setInputText('');
        setIsLoading(true);

        // Create assistant message with empty content
        const assistantMessage = {
            role: 'assistant',
            content: ''
        };
        setMessages(prev => [...prev, assistantMessage]);

        try {
            const messagePayload = [
                { role: 'system', content: getSystemPrompt() },
                ...messages,
                newMessage
            ];

            // Get the stream
            const stream = streamOpenAIResponse(messagePayload);
            let accumulatedContent = '';

            // Read and update the response chunk by chunk
            for await (const chunk of stream) {
                accumulatedContent += chunk;
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    lastMessage.content = accumulatedContent;
                    return [...newMessages];
                });
            }
        } catch (error) {
            console.error('Error streaming response:', error);
            setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].content = 'Error: Failed to get response';
                return newMessages;
            });
        } finally {
            setIsLoading(false);
        }
    }, [inputText, isLoading, messages, getSystemPrompt]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const MessageContent = ({ content, role }) => (
        <ReactMarkdown
            className={`prose prose-invert prose-sm max-w-none ${role === 'user' ? 'text-gray-200' : 'text-gray-200'
                }`}
            components={{
                // Style markdown elements
                p: ({ children }) => (
                    <p className="text-gray-200 mb-2 text-sm">{children}</p>
                ),
                h1: ({ children }) => (
                    <h1 className="text-purple-400 text-base font-bold mb-2">{children}</h1>
                ),
                h2: ({ children }) => (
                    <h2 className="text-purple-300 text-sm font-bold mb-2">{children}</h2>
                ),
                h3: ({ children }) => (
                    <h3 className="text-purple-200 text-sm font-bold mb-1">{children}</h3>
                ),
                ul: ({ children }) => (
                    <ul className="list-disc list-inside text-gray-200 mb-2 text-sm">{children}</ul>
                ),
                ol: ({ children }) => (
                    <ol className="list-decimal list-inside text-gray-200 mb-2 text-sm">{children}</ol>
                ),
                li: ({ children }) => (
                    <li className="text-gray-200 mb-1 text-sm">{children}</li>
                ),
                code: ({ inline, children }) => (
                    inline ?
                        <code className="bg-purple-900/20 text-purple-300 px-1 py-0.5 rounded text-sm">{children}</code> :
                        <pre className="bg-gray-900 p-2 rounded-md overflow-x-auto text-sm">
                            <code className="text-purple-300">{children}</code>
                        </pre>
                ),
                blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-purple-500 pl-2 italic text-gray-400 text-sm">
                        {children}
                    </blockquote>
                ),
            }}
        >
            {content}
        </ReactMarkdown>
    );

    // Add mouse event handlers
    const handleMouseDown = (e) => {
        // Only allow dragging from the header
        if (e.target.closest('.chat-header')) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - windowPosition.x,
                y: e.clientY - windowPosition.y
            });
        }
    };

    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            setWindowPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
    }, [isDragging, dragOffset]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add and remove event listeners
    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <div
            className="fixed bg-gray-800 rounded-lg shadow-lg border border-purple-500"
            style={{
                top: `${windowPosition.y}px`,
                left: `${windowPosition.x}px`,
                width: '350px',
                maxHeight: '500px',
                zIndex: 1000,
                cursor: isDragging ? 'grabbing' : 'auto'
            }}
            onMouseDown={handleMouseDown}
        >
            <div className="p-3 border-b border-purple-500 flex justify-between items-center chat-header cursor-grab">
                <h3 className="text-sm text-gray-200">Chat about selection</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-200"
                >
                    ×
                </button>
            </div>
            <div className="flex flex-col h-[400px]">
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    <div className="text-sm text-gray-400">
                        Selected text:
                        <div className="text-gray-200 italic mt-1 text-xs">
                            {selectedText}
                        </div>
                    </div>
                    {messages.map((message, index) => (
                        <div
                            key={index}
                            className={`p-2 rounded ${message.role === 'user'
                                ? 'bg-purple-900 ml-4'
                                : 'bg-gray-700 mr-4'
                                }`}
                        >
                            <MessageContent content={message.content} role={message.role} />
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-center">
                            <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}
                </div>
                <div className="p-3 border-t border-gray-700">
                    <textarea
                        className="w-full bg-gray-700 text-gray-200 rounded p-2 text-sm"
                        placeholder="Ask a question about this selection..."
                        rows={2}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                    />
                    <button
                        className={`mt-2 px-3 py-1 rounded text-sm w-full ${isLoading
                            ? 'bg-gray-600 text-gray-400'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                        onClick={handleSend}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SelectionChatWindow; 